import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { getVisibleSections, resolveView } from './helpData.js';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';

/**
 * Slide-in help drawer. Content is filtered to the signed-in user's role (see
 * helpData getVisibleSections) so each role only sees help for features it can
 * reach. Opens to the topic most relevant to the current route. Closes on the
 * backdrop click or Escape; locks body scroll while open.
 *
 * Styled with inline styles (the app uses Bootstrap, not Tailwind) so it renders
 * consistently without extra CSS.
 */
export default function HelpDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;

  const sections = useMemo(() => getVisibleSections(role), [role]);

  const [activeSection, setActiveSection] = useState('');
  const [activeTopic, setActiveTopic] = useState('');

  // When opening (or navigating while closed), jump to the route-relevant view.
  useEffect(() => {
    if (!isOpen) {
      const { sectionId, topicId } = resolveView(location.pathname, role);
      setActiveSection(sectionId || '');
      setActiveTopic(topicId || '');
    }
  }, [location.pathname, role, isOpen]);

  // Escape closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    const section = sections.find((s) => s.id === sectionId);
    if (section?.topics.length) setActiveTopic(section.topics[0].id);
  };

  const currentSection =
    sections.find((s) => s.id === activeSection) || sections[0] || null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 1080,
        }}
      />
      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Help and Documentation"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100%',
          width: 'min(560px, 100vw)',
          background: '#fff',
          zIndex: 1081,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header — brand gradient */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
          }}
        >
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <i className="fa fa-circle-question me-2" />
            Help &amp; Documentation
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="btn btn-sm"
            style={{ color: '#fff', background: 'transparent', border: 'none' }}
          >
            <i className="fa fa-xmark fa-lg" />
          </button>
        </div>

        {currentSection ? (
          <>
            <HelpNav
              sections={sections}
              activeSection={currentSection.id}
              onSectionChange={handleSectionChange}
            />
            <HelpContent
              section={currentSection}
              activeTopic={activeTopic}
              onTopicChange={setActiveTopic}
            />
          </>
        ) : (
          <div className="p-4 text-muted">No help topics are available for your role.</div>
        )}
      </div>
    </>
  );
}
